import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { WideDropdownPopper } from './field-auto-complete';
import { fetch } from '../utils/api-wrapper';

// Fallback when the currency-rates service is unreachable. DEFAULT_CURRENCY
// must match `DEFAULT_EXPENSE_CURRENCY` in xpens-api utils/client_defaults.py.
export const FALLBACK_CURRENCIES = ['CAD', 'USD', 'EUR'];
export const DEFAULT_CURRENCY = 'CAD';

const CACHE_TTL_MS = 60 * 60 * 1000;
let _cache = null;
let _cachedAt = 0;
let _inflight = null;

export const _loadCurrencies = () => {
    if (_cache && Date.now() - _cachedAt < CACHE_TTL_MS) {
        return Promise.resolve({ currencies: _cache, unavailable: false });
    }
    if (_inflight) return _inflight;
    // Empty list = currency-rates up but Firestore has no observation yet;
    // treat as unavailable so the note shows and the next mount retries.
    const promise = new Promise((resolve) => {
        fetch(
            '/currencies',
            'GET',
            {},
            (response) => {
                const currencies = response?.data?.currencies || [];
                if (!currencies.length) {
                    resolve({ currencies: [], unavailable: true });
                    return;
                }
                _cache = currencies;
                _cachedAt = Date.now();
                resolve({ currencies: _cache, unavailable: false });
            },
            () => resolve({ currencies: [], unavailable: true }),
        );
    });
    // Release in .finally, not the callback — the mock api-wrapper resolves
    // synchronously in tests, and an in-callback null-out would run BEFORE the
    // outer `_inflight = promise` assignment and get overwritten.
    _inflight = promise;
    promise.finally(() => {
        if (_inflight === promise) _inflight = null;
    });
    return promise;
};

export const _resetCurrencyCacheForTests = () => {
    _cache = null;
    _cachedAt = 0;
    _inflight = null;
};

const buildOptions = (currencies, saved) => {
    const offered = currencies?.length ? currencies : FALLBACK_CURRENCIES;
    // Keep an unlisted saved value visible so a save cannot silently drop it.
    return offered.includes(saved) ? offered : [saved, ...offered];
};

const UNAVAILABLE_KEY = 'client_table.default_currency_list_unavailable';

const CurrencySelect = ({
    value,
    onChange,
    data,
    inputId,
    disabled = false,
    fallback = DEFAULT_CURRENCY,
}) => {
    const [state, setState] = useState(() => ({
        currencies: _cache || [],
        unavailable: false,
        loaded: _cache != null,
    }));

    useEffect(() => {
        if (state.loaded) return;
        let mounted = true;
        _loadCurrencies().then(({ currencies, unavailable }) => {
            if (mounted) setState({ currencies, unavailable, loaded: true });
        });
        return () => {
            mounted = false;
        };
    }, [state.loaded]);

    const saved = value || fallback;
    const options = buildOptions(state.currencies, saved);
    const unavailableLabel = state.unavailable ? data?.t?.read?.(UNAVAILABLE_KEY) : '';

    // Controlled inputValue — MUI does not track typed text on its own when
    // `value` is controlled, so filter never runs without this.
    const [inputValue, setInputValue] = useState(saved);
    useEffect(() => { setInputValue(saved); }, [saved]);
    // Show the full list until the admin has actually typed. Tracking the
    // onInputChange reason avoids comparing text — a full-typed match of the
    // current selection would otherwise re-expand the menu.
    const [typed, setTyped] = useState(false);
    // Focus swaps field-unselected → field-selected, which is what makes the
    // control read as plain text among the other data fields until clicked.
    const [focused, setFocused] = useState(false);

    return (
        <div className='form-field d-flex'>
            <Autocomplete
                id={inputId}
                options={options}
                value={saved}
                onChange={(_event, code, reason) => {
                    if (reason === 'createOption' || !options.includes(code)) return;
                    onChange(code);
                }}
                inputValue={inputValue}
                onInputChange={(_event, next, reason) => {
                    setTyped(reason === 'input');
                    setInputValue(reason === 'reset' ? saved : next);
                }}
                filterOptions={typed ? undefined : (opts) => opts}
                freeSolo
                disableClearable
                disabled={disabled}
                fullWidth
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                slotProps={{ paper: { sx: { mt: 0.5 } } }}
                // XPEN-3467: the app's other MUI Autocomplete — same anchor-width
                // pin as field-auto-complete, so it shares the same popper.
                PopperComponent={WideDropdownPopper}
                isOptionEqualToValue={(option, selectedValue) => option === selectedValue}
                renderOption={(props, option) => (
                    <li {...props} className={`dropdown-item text-wrap ${option === saved ? 'active' : ''}`}>
                        {option}
                    </li>
                )}
                renderInput={(params) => {
                    const { inputProps, InputProps } = params;
                    const { ref: muiInputRef, ...restInputProps } = inputProps;
                    const stateClass = focused ? 'field-selected' : 'field-unselected';
                    return (
                        <div ref={InputProps.ref} className='w-100'>
                            <input
                                {...restInputProps}
                                ref={muiInputRef}
                                type='text'
                                className={`form-control form-control-sm ${stateClass}`}
                                data-testid={inputId}
                                disabled={disabled}
                            />
                        </div>
                    );
                }}
            />
            {unavailableLabel && unavailableLabel !== UNAVAILABLE_KEY && (
                <small className='form-text text-muted font-italic'>
                    {unavailableLabel}
                </small>
            )}
        </div>
    );
};

export default CurrencySelect;
